import { ChangeEvent, useEffect, useState } from 'react'
import { IoClose } from 'react-icons/io5'
import { BetTypeEnum, LottoItem } from '../BetLotto.types'
import { useTranslation } from 'react-i18next'
import {
  CurrencyObject,
  formatBetPriceWithCurrencyObject,
} from '../../../utils/currency-utils'
import { useThemeFeatures } from '../../../hooks/useThemeFeatures'
import styles from './BetItem.module.css'

interface BetControlItemProps extends LottoItem {
  idx: number
  type: BetTypeEnum
  disable?: boolean
  showWinAmount?: boolean
  onDeleteItem: (type: BetTypeEnum, number: string, idx: number) => void
  onChangeSingleBetPrice: (
    type: BetTypeEnum,
    number: string,
    amount: number,
  ) => void
  currencyObject: CurrencyObject
}

const BetControlItem = ({
  idx,
  type,
  number,
  price,
  rate,
  winPrice,
  onDeleteItem,
  onChangeSingleBetPrice,
  disable,
  showWinAmount = true,
  currencyObject,
}: BetControlItemProps) => {
  const { t } = useTranslation()
  const themeFeatures = useThemeFeatures()
  const [sprice, setBetPrice] = useState<number>(price)

  useEffect(() => {
    setBetPrice(price)
  }, [price])

  return (
    <div
      key={`${number}-${type}-${idx}`}
      className={`flex items-center px-3 py-2 bg-[var(--betcontrols-card-bg)] border border-[var(--betcontrols-card-border)] rounded-lg ${styles.betItem}`}
    >
      <div className="flex items-center justify-between w-full gap-1">
        {/* Number - Fixed width */}
        <span className="text-[var(--caption-text)] text-center font-semibold text-sm w-auto">
          {number}
        </span>

        {/* Bet amount input */}
        <div className="w-[58px] shrink-0">
          <input
            type="number"
            aria-label={`Bet amount for number ${number}`}
            className="w-full px-1 py-1 text-center rounded-lg bg-[var(--bet-type-bg)] text-[var(--bet-type-text)] font-semibold text-sm outline-none border border-[var(--bet-type-input-border)] hover:bg-[var(--bet-type-hover-bg)] hover:text-[var(--bet-type-hover-text)] hover:border-[var(--bet-type-hover-border)] focus:bg-[var(--bet-type-hover-bg)] focus:text-[var(--bet-type-hover-text)] focus:border-[var(--bet-type-active-border)] appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            value={isNaN(sprice) ? 1 : sprice}
            disabled={disable}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setBetPrice(e.target.valueAsNumber)
              onChangeSingleBetPrice(type, number, e.target.valueAsNumber)
            }}
          />
        </div>

        {/* Win amount */}
        {showWinAmount && (
          <div className="flex items-center gap-1 w-auto">
            <span className="text-[var(--betcontrols-muted-text)] text-xs">
              {t('betDetailPanel.win')}
            </span>
            <span className="text-[var(--betcontrols-muted-text)] text-sm font-medium">
              {winPrice.toLocaleString()}
            </span>
          </div>
        )}

        {/* Rate - with bet amount button styling */}
        {showWinAmount && (
          <span
            className="bet-amount-button text-[var(--balance-text-color)] font-medium text-sm w-auto text-center py-0.5 px-2 rounded-lg"
            data-group="button"
          >
            {formatBetPriceWithCurrencyObject(
              rate,
              currencyObject,
              themeFeatures.hideCurrencySymbolInBetControls,
            )}
          </span>
        )}

        {/* Delete button */}
        <button
          type="button"
          onClick={() => onDeleteItem(type, number, idx)}
          className="w-auto cursor-pointer text-center"
        >
          <div
            className="rounded-full flex items-center justify-center bg-[var(--betcontrols-remove)] hover:bg-[var(--betcontrols-remove-hover)] transition-colors"
            style={{
              width: '20px',
              height: '20px',
            }}
          >
            <IoClose size={14} className="text-[var(--color-primary-50)]" />
          </div>
        </button>
      </div>
    </div>
  )
}

export default BetControlItem
